<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="far fa-calendar-alt" class="q-mb-sm" /> {{ $t('calendar') }}
        </div>

        <q-table
            :data="schedules"
            :columns="columns"
            :pagination.sync="server_pagination"
            @request="request"
            :loading="loading"
            :filter="filter"
            row-key="name"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[5,10,50,100]"
            binary-state-sort
            >

            <template slot="top" slot-scope="props">
                <q-search v-model="filter" icon="fas fa-search" :placeholder="$t('search')" :debounce="500" />
                <div class="col" />
                <q-btn color="secondary" icon="fas fa-plus" :label="$t('new')" @click="$router.push({ name: 'schedule', params: { id: 0 } })" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="title" :props="props">
                    {{ props.row.title }}
                </q-td>

                <q-td key="permission" :props="props">
                    <q-btn color="primary" text-color="white" :label="getQuantity(props.row.permission)" no-ripple>
                        <q-tooltip>
                            <p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
                                {{ $t('areas') }}:
                                <span v-for="(area, index) in props.row.permission.areas" :key="index">
                                    {{ getArea(area) }}
                                    <span v-if="(props.row.permission.areas.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.profiles.length > 0" class="tooltip q-mb-sm">
                                {{ $t('profiles') }}:
                                <span v-for="(profile, index) in props.row.permission.profiles" :key="index">
                                    {{ getUserProfile(profile) }}
                                    <span v-if="(props.row.permission.profiles.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.user_groups.length > 0" class="tooltip q-mb-sm">
                                {{ $t('user_groups') }}:
                                <span v-for="(user_group, index) in props.row.permission.user_groups" :key="index">
                                    {{ getUserGroup(user_group) }}
                                    <span v-if="(props.row.permission.user_groups.length - 1) > index">, </span>
                                </span>
                            </p>
                        </q-tooltip>
                    </q-btn>
                </q-td>

                <q-td key="date_start" :props="props">
                    {{ props.row.date_start }}
                </q-td>

                <q-td key="date_end" :props="props">
                    {{ props.row.date_end }}
                </q-td>

                <q-td key="timezone" :props="props">
                    {{ props.row.timezone }}
                </q-td>

                <q-td key="actions" :props="props">
                    <q-btn flat round color="primary" icon="fas fa-pencil-alt" @click="$router.push({ name: 'schedule', params: { id: props.row.actions } })" >
                        <q-tooltip>
                            {{ $t('view_or_edit_tooltip')}}
                        </q-tooltip>
                    </q-btn>
                    <q-btn flat round color="negative" icon="far fa-trash-alt" @click="confirmRemove(props.row.actions)" >
                        <q-tooltip>
                            {{ $t('delete_tooltip')}}
                        </q-tooltip>
                    </q-btn>
                </q-td>
            </q-tr>
        </q-table>

        <q-dialog v-model="confirm_remove" @ok="removeItem">
            <span slot="title">{{ $t('remove_item') }}</span>

            <span slot="message">{{ $t('confirm_remove_item') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
            </template>
        </q-dialog>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import { LocalStorage } from 'quasar'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
    i18nOptions: { namespaces: [ 'custom', 'common', 'messages', 'admin'] },

    data() {
        return {
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: null,
                descending: false,
                sortBy: null
            },
            loading: false,
            schedules: [],
            columns: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'name',
                    sortable: false,
                },
                {
                    name: 'title',
                    required: true,
                    label: 'title',
                    align: 'left',
                    field: 'title',
                    sortable: false,
                },
                {
                    name: 'permission',
                    required: true,
                    label: 'send_group',
                    align: 'left',
                    field: 'permission',
                    sortable: false,
                },
                {
                    name: 'date_start',
                    required: true,
                    label: 'date_start',
                    align: 'left',
                    field: 'date_start',
                    sortable: false,
                },
                {
                    name: 'date_end',
                    required: true,
                    label: 'date_end',
                    align: 'left',
                    field: 'date_end',
                    sortable: false,
                },
                {
                    name: 'timezone',
                    required: true,
                    label: 'timezone',
                    align: 'left',
                    field: 'timezone',
                    sortable: false,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'actions',
                    sortable: false,
                },
            ],
            filter: '',
            action_id: '',
            confirm_remove: false
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            areas: 'registration_lists/country_areas',
            user_groups: 'registration_lists/group_users',
            profiles: 'registration_lists/profiles',
            id_user_language_selected: 'common/id_user_language_selected',
            num_rows: 'datatable/num_rows'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        }
    },

    methods: {
        request ({ pagination }) {
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "search": this.filter,
                "ordering": order_by
            }

            this.savePagination()

            this.$axios.get('/admin_calendar/calendar/?' + this.$qs.stringify(data), this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count

                this.schedules = response.data.results.map(r => {
                    return {
                        ...r,
                        date_start: moment(r.date_start).tz(r.timezone ? r.timezone : 'Etc/GMT').format('DD/MM/YYYY HH:mm:ss'),
                        date_end: moment(r.date_end).tz(r.timezone ? r.timezone : 'Etc/GMT').format('DD/MM/YYYY HH:mm:ss'),
                        actions: r.id,
                        provider: r.webcast_type,
                        permission: {
                            areas: r.areas,
                            profiles: r.profiles,
                            clients: r.clients,
                            user_groups: r.user_groups
                        },
                        timezone: `GMT ${moment.tz(new Date(), r.timezone).format('Z')}`
                    }
                })
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.loading = false
            })
        },

        fetchLists() {
            this.$q.loading.show()

            this.$axios.get('/accounts/get_registration_lists/', this.getHeader)
            .then(response => {
                this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        confirmRemove(id) {
            this.action_id = id
            this.confirm_remove = true
        },

        removeItem() {
            this.$q.loading.show()

            this.$axios.delete(`/admin_calendar/calendar/${this.action_id}/`, this.getHeader)
            .then(response => {
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })

                this.confirm_remove = false

                this.$q.notify({
                    message: this.$t('item_removed_success'),
                    type: 'positive',
                    icon: 'far fa-trash-alt'
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        getStatus(status) {
            if (status == 1) return 'scheduled'
            else if (status == 2) return 'sent'
            else if (status == 3) return 'deleted'
            else if (status == 4) return 'sending'
            else if (status == 5) return 'send_fail'
        },

        getIconStatus(status) {
            if (status == 1) return 'fas fa-clock'
            else if (status == 2) return 'fas fa-check'
            else if (status == 3) return 'fas fa-trash-alt'
            else if (status == 4) return 'fas fa-hourglass-half'
            else if (status == 5) return 'fas fa-ban'
        },

        getColorStatus(status) {
            if (status == 1) return 'warning'
            else if (status == 2) return 'positive'
            else if (status == 3) return 'negative'
            else if (status == 4) return 'info'
            else if (status == 5) return 'negative'
        },

        getQuantity(permission) {
            return permission.areas.length + permission.user_groups.length + permission.profiles.length
        },

        getArea(area) {
            let obj = this.areas.find((obj) => { return obj.value === area })
            if (obj) return obj.label
            else return ''
        },

        getUserGroup(user_group) {
            let obj = this.user_groups.find((obj) => { return obj.value === user_group })
            if (obj) return obj.label
            else return ''
        },

        getUserProfile(profile) {
            let obj = this.profiles.find((obj) => { return obj.value === profile })
            if (obj) return obj.label
            else return ''
        },

        savePagination() {
            this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
        },

        loadPagination() {
            this.server_pagination.rowsPerPage = this.num_rows
        }
    },

    created() {
        this.loadPagination()
        this.fetchLists()
    }
}
</script>

<style lang="stylus" scoped>

</style>
